import React from 'react';
import { IconPlus } from '@tabler/icons-react';
import { createRandomID } from '@/utils/random-id';
import { Badge } from '@/admin/components/ui/badge';
import { Button } from '@/admin/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/admin/components/ui/card';
import { Input } from '@/admin/components/ui/input';
import { Label } from '@/admin/components/ui/label';
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/admin/components/ui/sheet';
import { Switch } from '@/admin/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/admin/components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/admin/components/ui/tabs';
import { Textarea } from '@/admin/components/ui/textarea';
import {
  fetchLLMConfigStatus,
  fetchLLMModels,
  fetchLLMProviders,
  fetchLLMRoutes,
  fetchLLMServiceBindings,
  createWillowGroupOperation,
  fetchWillowAgentGroups,
  fetchWillowInitialAgents,
  fetchWillowTemplates,
  previewWillowGroupOperation,
  clearWillowInitialAgentAvatar,
  saveLLMModel,
  saveLLMProvider,
  saveLLMRoute,
  saveLLMServiceBinding,
  saveWillowInitialAgent,
  saveWillowTemplate,
  setMainWillowInitialAgent,
  uploadWillowInitialAgentAvatar,
  type JSONRecord,
  type LLMConfigStatus,
  type LLMModel,
  type LLMProvider,
  type LLMRoute,
  type LLMServiceBinding,
  type WillowAgentGroup,
  type WillowGroupOperationJob,
  type WillowInitialAgent,
  type WillowTemplate,
} from '@/admin/lib/api';

type EntityKind =
  | 'provider'
  | 'model'
  | 'route'
  | 'template'
  | 'initial-agent'
  | 'service-binding';

type SettingsSection =
  | 'providers'
  | 'models'
  | 'routes'
  | 'templates'
  | 'initial-agents'
  | 'service-bindings';

type DataEndpoint =
  | 'providers'
  | 'models'
  | 'routes'
  | 'templates'
  | 'initial-agents'
  | 'service-bindings'
  | 'status';

type LLMSettingsPageProps = {
  title?: string;
  description?: string;
  sections?: readonly SettingsSection[];
  showStatus?: boolean;
  showMainAgentCard?: boolean;
};

const GROUP_SYNC_PAGE_SIZE = 500;

function createOperationIdempotencyKey() {
  return createRandomID();
}

type Draft = Record<string, string | boolean | null>;

type Field =
  | { key: string; label: string; type?: 'text' | 'password' | 'number' }
  | { key: string; label: string; type: 'textarea'; rows?: number }
  | { key: string; label: string; type: 'json' }
  | { key: string; label: string; type: 'boolean' }
  | { key: string; label: string; type: 'boolean-nullable' }
  | { key: string; label: string; type: 'select'; options: SelectOption[] };

type SelectOption = { value: string; label: string };

const emptyJSON = '{}';
const noneValue = '__none__';
const initialAgentRoleOptions: SelectOption[] = [
  { value: 'worker', label: 'Worker' },
  { value: 'router', label: 'Router' },
];

const sectionDefinitions: readonly {
  value: SettingsSection;
  label: string;
}[] = [
  { value: 'providers', label: 'Providers' },
  { value: 'models', label: 'Models' },
  { value: 'routes', label: 'Routes' },
  { value: 'templates', label: 'Willow Templates' },
  { value: 'initial-agents', label: 'Initial Agents' },
  { value: 'service-bindings', label: 'Service Bindings' },
];

const allSections: (typeof sectionDefinitions)[number]['value'][] =
  sectionDefinitions.map(section => section.value);

const platformProvidersSections = ['providers'] as const;
const platformModelsSections = ['models'] as const;
const platformRoutesSections = ['routes'] as const;
const platformServiceBindingsSections = ['service-bindings'] as const;
const willowTemplatesSections = ['templates'] as const;
const willowInitialAgentsSections = ['initial-agents'] as const;

function getRequiredDataEndpoints(
  sections: readonly SettingsSection[],
  options: { showStatus: boolean; showMainAgentCard: boolean }
): ReadonlySet<DataEndpoint> {
  const endpoints = new Set<DataEndpoint>();
  for (const section of sections) {
    switch (section) {
      case 'providers':
        endpoints.add('providers');
        break;
      case 'models':
        endpoints.add('providers');
        endpoints.add('models');
        break;
      case 'routes':
        endpoints.add('providers');
        endpoints.add('models');
        endpoints.add('routes');
        break;
      case 'templates':
        endpoints.add('providers');
        endpoints.add('models');
        endpoints.add('templates');
        break;
      case 'initial-agents':
        endpoints.add('templates');
        endpoints.add('initial-agents');
        break;
      case 'service-bindings':
        endpoints.add('providers');
        endpoints.add('models');
        endpoints.add('routes');
        endpoints.add('templates');
        endpoints.add('service-bindings');
        break;
    }
  }
  if (options.showMainAgentCard) {
    endpoints.add('templates');
    endpoints.add('initial-agents');
  }
  if (options.showStatus) {
    endpoints.add('status');
  }
  return endpoints;
}

export const LLMSettingsPage: React.FC<LLMSettingsPageProps> = ({
  title = 'LLM Settings',
  description = 'Configure provider, model, route, and Willow template records. Templates are read from and saved directly to Willow.',
  sections = allSections,
  showStatus = true,
  showMainAgentCard,
}) => {
  const visibleSections = React.useMemo(
    () => (sections.length > 0 ? sections : allSections),
    [sections]
  );
  const displayMainAgentCard =
    showMainAgentCard ?? visibleSections.includes('initial-agents');
  const requiredData = React.useMemo(
    () =>
      getRequiredDataEndpoints(visibleSections, {
        showStatus,
        showMainAgentCard: displayMainAgentCard,
      }),
    [displayMainAgentCard, showStatus, visibleSections]
  );
  const [providers, setProviders] = React.useState<LLMProvider[]>([]);
  const [models, setModels] = React.useState<LLMModel[]>([]);
  const [routes, setRoutes] = React.useState<LLMRoute[]>([]);
  const [templates, setTemplates] = React.useState<WillowTemplate[]>([]);
  const [initialAgents, setInitialAgents] = React.useState<
    WillowInitialAgent[]
  >([]);
  const [bindings, setBindings] = React.useState<LLMServiceBinding[]>([]);
  const [status, setStatus] = React.useState<LLMConfigStatus | null>(null);
  const [activeTab, setActiveTab] = React.useState<SettingsSection>(
    visibleSections[0] ?? 'providers'
  );
  const [search, setSearch] = React.useState('');
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [savingMainAgent, setSavingMainAgent] = React.useState(false);
  const [syncingExistingGroups, setSyncingExistingGroups] =
    React.useState(false);
  const [existingGroupSyncJob, setExistingGroupSyncJob] =
    React.useState<WillowGroupOperationJob | null>(null);
  const [avatarSavingID, setAvatarSavingID] = React.useState<string | null>(
    null
  );
  const [error, setError] = React.useState<string | null>(null);
  const [editing, setEditing] = React.useState<{
    kind: EntityKind;
    id?: string;
    title: string;
    draft: Draft;
  } | null>(null);
  const [fieldErrors, setFieldErrors] = React.useState<Record<string, string>>(
    {}
  );

  const providerOptions = React.useMemo(
    () =>
      providers.map(row => ({
        value: row.id,
        label: `${row.key} (${row.provider_type})`,
      })),
    [providers]
  );
  const modelOptions = React.useMemo(
    () => models.map(row => ({ value: row.id, label: row.key })),
    [models]
  );
  const routeOptions = React.useMemo(
    () => routes.map(row => ({ value: row.id, label: row.key })),
    [routes]
  );
  const templateOptions = React.useMemo(
    () => templates.map(row => ({ value: row.id, label: row.name || row.id })),
    [templates]
  );
  const configuredMainAgent = React.useMemo(
    () =>
      initialAgents.find(row => row.enabled && row.slot === 'main') ??
      initialAgents.find(row => row.enabled && row.is_default) ??
      null,
    [initialAgents]
  );
  const selectableMainTemplateCount = templates.length;
  const [mainAgentDraft, setMainAgentDraft] = React.useState({
    template_id: '',
    display_name: '',
    description: '',
  });
  const selectedMainTemplate = React.useMemo(
    () => templates.find(row => row.id === mainAgentDraft.template_id) ?? null,
    [mainAgentDraft.template_id, templates]
  );

  React.useEffect(() => {
    setMainAgentDraft({
      template_id: configuredMainAgent?.template_id ?? '',
      display_name: configuredMainAgent?.display_name ?? '',
      description: configuredMainAgent?.description ?? '',
    });
  }, [
    configuredMainAgent?.description,
    configuredMainAgent?.display_name,
    configuredMainAgent?.template_id,
  ]);

  const loadAll = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [
        providerData,
        modelData,
        routeData,
        templateData,
        initialData,
        bindingData,
        statusData,
      ] = await Promise.all([
        requiredData.has('providers')
          ? fetchLLMProviders()
          : Promise.resolve(null),
        requiredData.has('models') ? fetchLLMModels() : Promise.resolve(null),
        requiredData.has('routes') ? fetchLLMRoutes() : Promise.resolve(null),
        requiredData.has('templates')
          ? fetchWillowTemplates()
          : Promise.resolve(null),
        requiredData.has('initial-agents')
          ? fetchWillowInitialAgents()
          : Promise.resolve(null),
        requiredData.has('service-bindings')
          ? fetchLLMServiceBindings()
          : Promise.resolve(null),
        requiredData.has('status')
          ? fetchLLMConfigStatus()
          : Promise.resolve(null),
      ]);
      setProviders(providerData?.providers ?? []);
      setModels(modelData?.models ?? []);
      setRoutes(routeData?.routes ?? []);
      setTemplates(templateData?.templates ?? []);
      setInitialAgents(initialData?.initial_agents ?? []);
      setBindings(bindingData?.service_bindings ?? []);
      setStatus(statusData);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to load LLM settings'
      );
    } finally {
      setLoading(false);
    }
  }, [requiredData]);

  React.useEffect(() => {
    loadAll();
  }, [loadAll]);

  React.useEffect(() => {
    if (!visibleSections.includes(activeTab)) {
      setActiveTab(visibleSections[0] ?? 'providers');
    }
  }, [activeTab, visibleSections]);

  const fields = React.useMemo(
    () =>
      getFields(editing?.kind, {
        providerOptions,
        modelOptions,
        routeOptions,
        templateOptions,
      }),
    [
      editing?.kind,
      modelOptions,
      providerOptions,
      routeOptions,
      templateOptions,
    ]
  );

  const openCreate = (kind: EntityKind) => {
    setFieldErrors({});
    setEditing({ kind, title: createTitle(kind), draft: defaultDraft(kind) });
  };

  const openEdit = (
    kind: EntityKind,
    row:
      | LLMProvider
      | LLMModel
      | LLMRoute
      | WillowTemplate
      | WillowInitialAgent
      | LLMServiceBinding
  ) => {
    setFieldErrors({});
    setEditing({
      kind,
      id: row.id,
      title: editTitle(kind),
      draft: draftFromRow(kind, row),
    });
  };

  const handleSave = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!editing) return;
    const parsed = parseDraft(editing.kind, editing.draft);
    if (!parsed.ok) {
      setFieldErrors(parsed.errors);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      switch (editing.kind) {
        case 'provider':
          await saveLLMProvider(parsed.value, editing.id);
          break;
        case 'model':
          await saveLLMModel(parsed.value, editing.id);
          break;
        case 'route':
          await saveLLMRoute(parsed.value, editing.id);
          break;
        case 'template':
          await saveWillowTemplate(parsed.value, editing.id);
          break;
        case 'initial-agent':
          await saveWillowInitialAgent(parsed.value, editing.id);
          break;
        case 'service-binding':
          await saveLLMServiceBinding(parsed.value, editing.id);
          break;
      }
      setEditing(null);
      await loadAll();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to save LLM settings'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleSetMainAgent = async () => {
    const templateID = mainAgentDraft.template_id.trim();
    const displayName = mainAgentDraft.display_name.trim();
    if (!templateID || !displayName) {
      setError('Select a Willow template and enter a display name.');
      return;
    }
    setSavingMainAgent(true);
    setError(null);
    try {
      await setMainWillowInitialAgent({
        template_id: templateID,
        display_name: displayName,
        description: mainAgentDraft.description.trim(),
      });
      await loadAll();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to set main initial agent'
      );
    } finally {
      setSavingMainAgent(false);
    }
  };

  const fetchAllWillowGroupIDs = async () => {
    const ids: string[] = [];
    let offset = 0;
    for (;;) {
      // eslint-disable-next-line no-await-in-loop
      const response = await fetchWillowAgentGroups({
        limit: GROUP_SYNC_PAGE_SIZE,
        offset,
      });
      ids.push(
        ...response.groups
          .map((group: WillowAgentGroup) => group.group_id.trim())
          .filter(Boolean)
      );
      const pageLimit = response.limit || GROUP_SYNC_PAGE_SIZE;
      offset += pageLimit;
      if (
        response.groups.length === 0 ||
        ids.length >= response.total_count ||
        response.groups.length < pageLimit
      ) {
        return ids;
      }
    }
  };

  const handleApplyDefaultSeedToExistingGroups = async () => {
    const templateID = configuredMainAgent?.template_id?.trim();
    if (!templateID) {
      setError('Configure the default main seed before applying it to groups.');
      return;
    }
    setSyncingExistingGroups(true);
    setExistingGroupSyncJob(null);
    setError(null);
    try {
      const groupIDs = await fetchAllWillowGroupIDs();
      if (groupIDs.length === 0) {
        setError('No Willow groups found to sync.');
        return;
      }
      const baseInput = {
        operation_type: 'update_home_agent_template' as const,
        group_ids: groupIDs,
        params: { target_template_id: templateID },
        reason:
          'Apply default main seed template to existing group home agents',
      };
      const preview = await previewWillowGroupOperation(baseInput);
      const confirmed = window.confirm(
        [
          `Queue in-place template updates for ${groupIDs.length} existing groups?`,
          `Affected: ${preview.affected_count}. Already target: ${preview.already_target_count}. Expected failures: ${preview.expected_failure_count}.`,
          'This preserves every group→home_agent_id binding and agent history.',
        ].join('\n\n')
      );
      if (!confirmed) {
        return;
      }
      const job = await createWillowGroupOperation({
        ...baseInput,
        idempotency_key: createOperationIdempotencyKey(),
      });
      setExistingGroupSyncJob(job);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to apply seed to existing groups'
      );
    } finally {
      setSyncingExistingGroups(false);
    }
  };

  const handleUploadInitialAgentAvatar = async (
    row: WillowInitialAgent,
    file: File | null | undefined
  ) => {
    if (!file) return;
    setAvatarSavingID(row.id);
    setError(null);
    try {
      await uploadWillowInitialAgentAvatar(row.id, file);
      await loadAll();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to upload initial agent avatar'
      );
    } finally {
      setAvatarSavingID(null);
    }
  };

  const handleClearInitialAgentAvatar = async (row: WillowInitialAgent) => {
    setAvatarSavingID(row.id);
    setError(null);
    try {
      await clearWillowInitialAgentAvatar(row.id);
      await loadAll();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to clear initial agent avatar'
      );
    } finally {
      setAvatarSavingID(null);
    }
  };

  const normalizedSearch = search.trim().toLowerCase();
  const filterRows = <T,>(rows: T[]) =>
    normalizedSearch === ''
      ? rows
      : rows.filter(row =>
          JSON.stringify(row).toLowerCase().includes(normalizedSearch)
        );
  const currentSection = visibleSections.includes(activeTab)
    ? activeTab
    : (visibleSections[0] ?? 'providers');
  const displayTabs = visibleSections.length > 1;
  const visibleSectionDefinitions = sectionDefinitions.filter(section =>
    visibleSections.includes(section.value)
  );
  const renderSection = (section: SettingsSection) => {
    switch (section) {
      case 'providers':
        return (
          <SettingsTable
            title="Providers"
            loading={loading}
            onCreate={() => openCreate('provider')}
            headers={['Key', 'Type', 'Base URL', 'Mode', 'Status', '']}
            rows={filterRows(providers).map(row => [
              row.key,
              row.provider_type,
              row.base_url || 'default',
              row.mode || 'default',
              <EnabledBadge key="enabled" enabled={row.enabled} />,
              <Button
                key="edit"
                variant="outline"
                size="sm"
                onClick={() => openEdit('provider', row)}
              >
                Edit
              </Button>,
            ])}
          />
        );
      case 'models':
        return (
          <SettingsTable
            title="Models"
            loading={loading}
            onCreate={() => openCreate('model')}
            headers={['Key', 'Provider', 'Upstream', 'Namespace', 'Status', '']}
            rows={filterRows(models).map(row => [
              row.key,
              providerLabel(row.provider_id, providers),
              row.upstream_model,
              row.model_namespace || 'default',
              <EnabledBadge key="enabled" enabled={row.enabled} />,
              <Button
                key="edit"
                variant="outline"
                size="sm"
                onClick={() => openEdit('model', row)}
              >
                Edit
              </Button>,
            ])}
          />
        );
      case 'routes':
        return (
          <SettingsTable
            title="Routes"
            loading={loading}
            onCreate={() => openCreate('route')}
            headers={[
              'Key',
              'Purpose',
              'Primary model',
              'Fallbacks',
              'Status',
              '',
            ]}
            rows={filterRows(routes).map(row => [
              row.key,
              row.purpose,
              modelLabel(row.model_id, models),
              String(row.fallback_model_ids.length),
              <EnabledBadge key="enabled" enabled={row.enabled} />,
              <Button
                key="edit"
                variant="outline"
                size="sm"
                onClick={() => openEdit('route', row)}
              >
                Edit
              </Button>,
            ])}
          />
        );
      case 'templates':
        return (
          <SettingsTable
            title="Willow Templates"
            loading={loading}
            onCreate={() => openCreate('template')}
            headers={[
              'Template ID',
              'Name',
              'Source model',
              'Source provider',
              '',
            ]}
            rows={filterRows(templates).map(row => [
              row.id,
              row.name,
              templateSourceModelLabel(row, models),
              templateSourceProviderLabel(row, providers),
              <Button
                key="edit"
                variant="outline"
                size="sm"
                onClick={() => openEdit('template', row)}
              >
                Edit
              </Button>,
            ])}
          />
        );
      case 'initial-agents':
        return (
          <SettingsTable
            title="Initial Agents"
            loading={loading}
            onCreate={() => openCreate('initial-agent')}
            headers={[
              'Slot',
              'Display',
              'Description',
              'Template',
              'Role',
              'Avatar',
              'Default',
              'Default router seed',
              'Sort',
              'Status',
              '',
            ]}
            rows={filterRows(initialAgents).map(row => [
              row.slot,
              row.display_name,
              <span key="description" className="text-muted-foreground">
                {row.description || '—'}
              </span>,
              templateLabel(row.template_id, templates),
              initialAgentRoleLabel(row),
              <InitialAgentAvatarCell
                key="avatar"
                row={row}
                busy={avatarSavingID === row.id}
                onUpload={handleUploadInitialAgentAvatar}
                onClear={handleClearInitialAgentAvatar}
              />,
              row.is_default ? (row.slot === 'main' ? 'Main' : 'Yes') : 'No',
              row.is_router ? 'Seed' : 'No',
              String(row.sort_order),
              <EnabledBadge key="enabled" enabled={row.enabled} />,
              <Button
                key="edit"
                variant="outline"
                size="sm"
                onClick={() => openEdit('initial-agent', row)}
              >
                Edit
              </Button>,
            ])}
          />
        );
      case 'service-bindings':
        return (
          <SettingsTable
            title="Service Bindings"
            loading={loading}
            onCreate={() => openCreate('service-binding')}
            headers={['Service', 'Provider', 'Model', 'Route', 'Status', '']}
            rows={filterRows(bindings).map(row => [
              row.service_key,
              providerLabel(row.provider_id, providers),
              modelLabel(row.model_id, models),
              routeLabel(row.route_id, routes),
              <EnabledBadge key="enabled" enabled={row.enabled} />,
              <Button
                key="edit"
                variant="outline"
                size="sm"
                onClick={() => openEdit('service-binding', row)}
              >
                Edit
              </Button>,
            ])}
          />
        );
    }
  };

  return (
    <div className="flex flex-col gap-6 p-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-normal">{title}</h1>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
      </div>

      {showStatus && (
        <div className="grid gap-3 md:grid-cols-2">
          <StatusTile label="Source" value={status?.source_mode ?? 'unknown'} />
          <StatusTile label="Revision" value={String(status?.revision ?? 0)} />
        </div>
      )}

      {error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {displayMainAgentCard && (
        <Card>
          <CardHeader>
            <CardTitle>Default Main Agent Seed</CardTitle>
            <CardDescription>
              Used only when Willow materializes a new group home agent.
              Existing groups keep their current home agent identity and
              history.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.2fr)_auto] lg:items-end">
            <div className="space-y-2">
              <Label htmlFor="main-agent-template">Template</Label>
              <select
                id="main-agent-template"
                value={mainAgentDraft.template_id || noneValue}
                onChange={event => {
                  const templateID =
                    event.target.value === noneValue ? '' : event.target.value;
                  const selected = templates.find(row => row.id === templateID);
                  setMainAgentDraft(prev => ({
                    ...prev,
                    template_id: templateID,
                    display_name: selected?.name || selected?.id || '',
                  }));
                }}
                className="border-input bg-background h-9 w-full rounded-md border px-3 text-sm"
              >
                <option value={noneValue}>Select Willow template</option>
                {templates.map(template => (
                  <option key={template.id} value={template.id}>
                    {template.name || template.id}
                  </option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground">
                Current default main slot:{' '}
                {configuredMainAgent
                  ? `${configuredMainAgent.display_name} (${configuredMainAgent.slot})`
                  : 'not configured'}
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="main-agent-display-name">Display name</Label>
              <Input
                id="main-agent-display-name"
                value={mainAgentDraft.display_name}
                onChange={event =>
                  setMainAgentDraft(prev => ({
                    ...prev,
                    display_name: event.target.value,
                  }))
                }
                placeholder="DeepSeek V4 Pro"
              />
              <p className="text-xs text-muted-foreground">
                Saving makes this template the `slot=main` seed for future group
                materialization only; existing groups keep their current home
                agents.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="main-agent-description">Description</Label>
              <Textarea
                id="main-agent-description"
                value={mainAgentDraft.description}
                onChange={event =>
                  setMainAgentDraft(prev => ({
                    ...prev,
                    description: event.target.value,
                  }))
                }
                rows={2}
                placeholder="Task selection guidance for peer agents"
              />
              <p className="text-xs text-muted-foreground">
                Used by Willow `list_agents` to help peers choose this slot.
              </p>
            </div>
            <div className="flex flex-col gap-2">
              <Button
                type="button"
                onClick={handleSetMainAgent}
                disabled={
                  loading ||
                  savingMainAgent ||
                  syncingExistingGroups ||
                  selectableMainTemplateCount === 0 ||
                  !selectedMainTemplate ||
                  !mainAgentDraft.template_id ||
                  !mainAgentDraft.display_name.trim()
                }
              >
                {savingMainAgent ? 'Saving' : 'Set default main seed'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={handleApplyDefaultSeedToExistingGroups}
                disabled={
                  loading ||
                  savingMainAgent ||
                  syncingExistingGroups ||
                  !configuredMainAgent?.template_id
                }
              >
                {syncingExistingGroups
                  ? 'Queueing sync…'
                  : 'Apply seed to existing groups'}
              </Button>
              <p className="text-xs text-muted-foreground">
                Apply queues a durable batch operation that updates each
                existing group&apos;s bound home agent in place.
              </p>
            </div>
          </CardContent>
          {existingGroupSyncJob ? (
            <CardContent className="pt-0">
              <div className="rounded-md border p-3 text-sm">
                Queued operation{' '}
                <a
                  className="font-medium underline"
                  href={`/willow/operations?job=${encodeURIComponent(existingGroupSyncJob.id)}`}
                >
                  {existingGroupSyncJob.id}
                </a>{' '}
                to apply this seed to existing groups.
              </div>
            </CardContent>
          ) : null}
        </Card>
      )}

      {displayTabs ? (
        <Tabs
          value={currentSection}
          onValueChange={value => setActiveTab(value as SettingsSection)}
          className="gap-4"
        >
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <TabsList className="flex h-auto flex-wrap justify-start">
              {visibleSectionDefinitions.map(section => (
                <TabsTrigger key={section.value} value={section.value}>
                  {section.label}
                </TabsTrigger>
              ))}
            </TabsList>
            <Input
              className="md:max-w-xs"
              value={search}
              onChange={event => setSearch(event.target.value)}
              placeholder="Filter current table"
            />
          </div>

          {visibleSectionDefinitions.map(section => (
            <TabsContent key={section.value} value={section.value}>
              {renderSection(section.value)}
            </TabsContent>
          ))}
        </Tabs>
      ) : (
        <div className="flex flex-col gap-3">
          <div className="flex justify-end">
            <Input
              className="md:max-w-xs"
              value={search}
              onChange={event => setSearch(event.target.value)}
              placeholder="Filter table"
            />
          </div>
          {renderSection(currentSection)}
        </div>
      )}

      <Sheet
        open={editing !== null}
        onOpenChange={open => !open && setEditing(null)}
      >
        <SheetContent className="w-full overflow-y-auto sm:max-w-2xl">
          <SheetHeader>
            <SheetTitle>{editing?.title}</SheetTitle>
          </SheetHeader>
          {editing && (
            <form
              onSubmit={handleSave}
              className="flex flex-1 flex-col gap-4 px-4"
            >
              {fields.map(field => (
                <DraftField
                  key={field.key}
                  field={field}
                  value={editing.draft[field.key]}
                  error={fieldErrors[field.key]}
                  onChange={value =>
                    setEditing(prev =>
                      prev
                        ? {
                            ...prev,
                            draft: { ...prev.draft, [field.key]: value },
                          }
                        : prev
                    )
                  }
                />
              ))}
              <SheetFooter className="px-0">
                <Button type="submit" disabled={saving}>
                  {saving ? 'Saving' : 'Save'}
                </Button>
              </SheetFooter>
            </form>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export const PlatformProvidersPage: React.FC = () => (
  <LLMSettingsPage
    title="Providers"
    description="Configure shared LLM provider connections used by Willow and other services."
    sections={platformProvidersSections}
    showStatus={false}
  />
);

export const PlatformModelsPage: React.FC = () => (
  <LLMSettingsPage
    title="Models"
    description="Configure shared model records and provider mappings."
    sections={platformModelsSections}
    showStatus={false}
  />
);

export const PlatformRoutesPage: React.FC = () => (
  <LLMSettingsPage
    title="Routes"
    description="Configure shared model routing rules and fallbacks."
    sections={platformRoutesSections}
    showStatus={false}
  />
);

export const PlatformServiceBindingsPage: React.FC = () => (
  <LLMSettingsPage
    title="Service Bindings"
    description="Map Cueboard services to shared providers, models, routes, and Willow templates."
    sections={platformServiceBindingsSections}
    showStatus={false}
  />
);

export const WillowTemplatesPage: React.FC = () => (
  <LLMSettingsPage
    title="Templates"
    description="Configure Willow template records used by agents and group operations."
    sections={willowTemplatesSections}
    showStatus={false}
  />
);

export const WillowInitialAgentsPage: React.FC = () => (
  <LLMSettingsPage
    title="Initial Agents"
    description="Configure Willow initial-agent defaults for future group materialization. Changes here do not update existing groups."
    sections={willowInitialAgentsSections}
    showStatus={false}
    showMainAgentCard
  />
);

function StatusTile({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          {label}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-xl font-semibold">{value}</div>
      </CardContent>
    </Card>
  );
}

function SettingsTable({
  title,
  headers,
  rows,
  loading,
  onCreate,
}: {
  title: string;
  headers: string[];
  rows: React.ReactNode[][];
  loading: boolean;
  onCreate?: () => void;
}) {
  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between">
        <CardTitle>{title}</CardTitle>
        {onCreate && (
          <Button onClick={onCreate} size="sm">
            <IconPlus className="size-4" />
            New
          </Button>
        )}
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              {headers.map(header => (
                <TableHead
                  key={header}
                  className={header === '' ? 'text-right' : undefined}
                >
                  {header}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={headers.length}>Loading</TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={headers.length}>No records</TableCell>
              </TableRow>
            ) : (
              rows.map(row => {
                const rowKey = String(row[0] ?? '');
                return (
                  <TableRow key={rowKey}>
                    {row.map((cell, cellIndex) => (
                      <TableCell
                        key={headers[cellIndex] || 'actions'}
                        className={
                          cellIndex === row.length - 1
                            ? 'text-right'
                            : undefined
                        }
                      >
                        {cell}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function InitialAgentAvatarCell({
  row,
  busy,
  onUpload,
  onClear,
}: {
  row: WillowInitialAgent;
  busy: boolean;
  onUpload: (row: WillowInitialAgent, file: File | null | undefined) => void;
  onClear: (row: WillowInitialAgent) => void;
}) {
  const inputID = `initial-agent-avatar-${row.id}`;
  return (
    <div className="flex items-center gap-2">
      {row.avatar_url ? (
        <img
          src={row.avatar_url}
          alt={`${row.display_name} avatar`}
          className="size-8 rounded-full border bg-muted object-cover"
        />
      ) : (
        <div className="flex size-8 items-center justify-center rounded-full border bg-muted text-[10px] text-muted-foreground">
          None
        </div>
      )}
      <div className="flex flex-col items-start gap-1">
        <div className="flex items-center gap-1">
          <input
            id={inputID}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="hidden"
            disabled={busy}
            onChange={event => {
              onUpload(row, event.target.files?.[0]);
              event.target.value = '';
            }}
          />
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={() => document.getElementById(inputID)?.click()}
          >
            {busy ? 'Saving' : row.avatar_url ? 'Replace' : 'Upload'}
          </Button>
          {row.avatar_url ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() => onClear(row)}
            >
              Clear
            </Button>
          ) : null}
        </div>
        {row.avatar_size_bytes ? (
          <span className="text-xs text-muted-foreground">
            {formatBytes(row.avatar_size_bytes)}
          </span>
        ) : null}
      </div>
    </div>
  );
}

function formatBytes(bytes: number) {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  return `${Math.round(bytes / 1024)} KB`;
}

function DraftField({
  field,
  value,
  error,
  onChange,
}: {
  field: Field;
  value: string | boolean | null | undefined;
  error?: string;
  onChange: (value: string | boolean | null) => void;
}) {
  const id = `llm-${field.key}`;
  if (field.type === 'boolean') {
    return (
      <div className="flex items-center justify-between rounded-md border p-3">
        <Label htmlFor={id}>{field.label}</Label>
        <Switch id={id} checked={Boolean(value)} onCheckedChange={onChange} />
      </div>
    );
  }
  if (field.type === 'boolean-nullable') {
    const selectValue =
      value === null || value === undefined ? '' : String(value);
    return (
      <div className="space-y-2">
        <Label htmlFor={id}>{field.label}</Label>
        <select
          id={id}
          value={selectValue}
          onChange={event => {
            const v = event.target.value;
            onChange(v === '' ? null : v === 'true');
          }}
          className="border-input bg-background h-9 w-full rounded-md border px-3 text-sm"
        >
          <option value="">Unset (no override)</option>
          <option value="true">Yes</option>
          <option value="false">No</option>
        </select>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{field.label}</Label>
      {field.type === 'json' ? (
        <Textarea
          id={id}
          value={String(value ?? emptyJSON)}
          onChange={event => onChange(event.target.value)}
          rows={7}
          className="font-mono text-sm"
        />
      ) : field.type === 'textarea' ? (
        <Textarea
          id={id}
          value={String(value ?? '')}
          onChange={event => onChange(event.target.value)}
          rows={field.rows ?? 3}
        />
      ) : field.type === 'select' ? (
        <select
          id={id}
          value={String(value || noneValue)}
          onChange={event =>
            onChange(event.target.value === noneValue ? '' : event.target.value)
          }
          className="border-input bg-background h-9 w-full rounded-md border px-3 text-sm"
        >
          <option value={noneValue}>None</option>
          {field.options.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      ) : (
        <Input
          id={id}
          type={field.type ?? 'text'}
          value={String(value ?? '')}
          onChange={event => onChange(event.target.value)}
        />
      )}
      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
}

function EnabledBadge({ enabled }: { enabled: boolean }) {
  return (
    <Badge variant={enabled ? 'default' : 'secondary'}>
      {enabled ? 'Enabled' : 'Disabled'}
    </Badge>
  );
}

function getFields(
  kind: EntityKind | undefined,
  options: {
    providerOptions: SelectOption[];
    modelOptions: SelectOption[];
    routeOptions: SelectOption[];
    templateOptions: SelectOption[];
  }
): Field[] {
  switch (kind) {
    case 'provider':
      return [
        { key: 'key', label: 'Key' },
        { key: 'display_name', label: 'Display name' },
        { key: 'provider_type', label: 'Provider type' },
        { key: 'base_url', label: 'Base URL' },
        { key: 'api_key', label: 'API key', type: 'password' },
        { key: 'auth_header', label: 'Auth header' },
        { key: 'mode', label: 'Mode' },
        { key: 'headers', label: 'Headers JSON', type: 'json' },
        { key: 'options', label: 'Options JSON', type: 'json' },
        { key: 'enabled', label: 'Enabled', type: 'boolean' },
      ];
    case 'model':
      return [
        { key: 'key', label: 'Key' },
        { key: 'display_name', label: 'Display name' },
        {
          key: 'provider_id',
          label: 'Provider',
          type: 'select',
          options: options.providerOptions,
        },
        { key: 'upstream_model', label: 'Upstream model' },
        { key: 'model_namespace', label: 'Model namespace' },
        { key: 'capabilities', label: 'Capabilities JSON', type: 'json' },
        { key: 'defaults', label: 'Defaults JSON', type: 'json' },
        { key: 'enabled', label: 'Enabled', type: 'boolean' },
      ];
    case 'route':
      return [
        { key: 'key', label: 'Key' },
        { key: 'purpose', label: 'Purpose' },
        {
          key: 'model_id',
          label: 'Primary model',
          type: 'select',
          options: options.modelOptions,
        },
        {
          key: 'fallback_model_ids',
          label: 'Fallback model IDs, one per line',
        },
        { key: 'options', label: 'Options JSON', type: 'json' },
        { key: 'enabled', label: 'Enabled', type: 'boolean' },
      ];
    case 'template':
      return [
        { key: 'name', label: 'Name' },
        {
          key: 'model_id',
          label: 'Source model',
          type: 'select',
          options: options.modelOptions,
        },
        {
          key: 'provider_id',
          label: 'Source provider',
          type: 'select',
          options: options.providerOptions,
        },
        { key: 'provider_config', label: 'Provider config JSON', type: 'json' },
        { key: 'request_headers', label: 'Request headers JSON', type: 'json' },
        { key: 'image_config', label: 'Image config JSON', type: 'json' },
        {
          key: 'vision_describer_config',
          label: 'Vision describer config JSON',
          type: 'json',
        },
        { key: 'analyze_config', label: 'Analyze config JSON', type: 'json' },
        { key: 'max_tokens', label: 'Max tokens', type: 'number' },
        { key: 'context_tokens', label: 'Context tokens', type: 'number' },
        { key: 'purpose', label: 'Purpose' },
        {
          key: 'supports_images',
          label: 'Supports images',
          type: 'boolean-nullable',
        },
        { key: 'hidden', label: 'Hidden', type: 'boolean' },
      ];
    case 'initial-agent':
      return [
        { key: 'slot', label: 'Slot' },
        { key: 'display_name', label: 'Display name' },
        {
          key: 'description',
          label: 'Description',
          type: 'textarea',
          rows: 3,
        },
        {
          key: 'template_id',
          label: 'Template',
          type: 'select',
          options: options.templateOptions,
        },
        {
          key: 'role',
          label: 'Role',
          type: 'select',
          options: initialAgentRoleOptions,
        },
        { key: 'sort_order', label: 'Sort order', type: 'number' },
        { key: 'is_default', label: 'Default', type: 'boolean' },
        { key: 'is_router', label: 'Default router seed', type: 'boolean' },
        { key: 'enabled', label: 'Enabled', type: 'boolean' },
      ];
    case 'service-binding':
      return [
        { key: 'service_key', label: 'Service key' },
        {
          key: 'provider_id',
          label: 'Provider',
          type: 'select',
          options: options.providerOptions,
        },
        {
          key: 'model_id',
          label: 'Model',
          type: 'select',
          options: options.modelOptions,
        },
        {
          key: 'route_id',
          label: 'Route',
          type: 'select',
          options: options.routeOptions,
        },
        { key: 'config', label: 'Config JSON', type: 'json' },
        { key: 'enabled', label: 'Enabled', type: 'boolean' },
      ];
    default:
      return [];
  }
}

function defaultDraft(kind: EntityKind): Draft {
  const base = { enabled: true };
  switch (kind) {
    case 'provider':
      return {
        ...base,
        key: '',
        display_name: '',
        provider_type: 'openai',
        base_url: '',
        api_key: '',
        auth_header: '',
        mode: '',
        headers: emptyJSON,
        options: emptyJSON,
      };
    case 'model':
      return {
        ...base,
        key: '',
        display_name: '',
        provider_id: '',
        upstream_model: '',
        model_namespace: '',
        capabilities: emptyJSON,
        defaults: emptyJSON,
      };
    case 'route':
      return {
        ...base,
        key: '',
        purpose: '',
        model_id: '',
        fallback_model_ids: '',
        options: emptyJSON,
      };
    case 'template':
      return {
        name: '',
        model_id: '',
        provider_id: '',
        provider_config: emptyJSON,
        request_headers: emptyJSON,
        image_config: emptyJSON,
        vision_describer_config: emptyJSON,
        analyze_config: emptyJSON,
        max_tokens: '65536',
        context_tokens: '128000',
        supports_images: null,
        hidden: false,
        purpose: '',
      };
    case 'initial-agent':
      return {
        ...base,
        slot: '',
        display_name: '',
        description: '',
        template_id: '',
        role: 'worker',
        sort_order: '0',
        is_default: false,
        is_router: false,
      };
    case 'service-binding':
      return {
        ...base,
        service_key: '',
        provider_id: '',
        model_id: '',
        route_id: '',
        config: emptyJSON,
      };
  }
}

function draftFromRow(
  kind: EntityKind,
  row:
    | LLMProvider
    | LLMModel
    | LLMRoute
    | WillowTemplate
    | WillowInitialAgent
    | LLMServiceBinding
): Draft {
  const copy = { ...row } as Record<string, unknown>;
  for (const key of Object.keys(copy)) {
    if (typeof copy[key] === 'object' && copy[key] !== null) {
      copy[key] = JSON.stringify(copy[key], null, 2);
    }
  }
  if (kind === 'route') {
    copy.fallback_model_ids = (row as LLMRoute).fallback_model_ids.join('\n');
  }
  return copy as Draft;
}

function parseDraft(
  kind: EntityKind,
  draft: Draft
):
  | { ok: true; value: Record<string, unknown> }
  | { ok: false; errors: Record<string, string> } {
  const errors: Record<string, string> = {};
  const fields = getFields(kind, {
    providerOptions: [],
    modelOptions: [],
    routeOptions: [],
    templateOptions: [],
  });
  const value: Record<string, unknown> = {};
  for (const field of fields) {
    const raw = draft[field.key];
    if (field.type === 'json') {
      const parsed = parseJSONObject(String(raw ?? emptyJSON));
      if (!parsed.ok) {
        errors[field.key] = parsed.error;
      } else {
        value[field.key] = parsed.value;
      }
    } else if (field.type === 'number') {
      value[field.key] = Number(raw || 0);
    } else if (field.type === 'boolean') {
      value[field.key] = raw;
    } else if (field.type === 'boolean-nullable') {
      value[field.key] = raw === null || raw === undefined ? null : raw;
    } else {
      value[field.key] = raw;
    }
  }
  if (kind === 'route') {
    value.fallback_model_ids = String(draft.fallback_model_ids ?? '')
      .split(/\s+/)
      .map(item => item.trim())
      .filter(Boolean);
  }
  if (Object.keys(errors).length > 0) {
    return { ok: false, errors };
  }
  return { ok: true, value };
}

function parseJSONObject(
  raw: string
): { ok: true; value: JSONRecord } | { ok: false; error: string } {
  try {
    const parsed = JSON.parse(raw || emptyJSON);
    if (!parsed || Array.isArray(parsed) || typeof parsed !== 'object') {
      return { ok: false, error: 'Must be a JSON object.' };
    }
    return { ok: true, value: parsed as JSONRecord };
  } catch (err) {
    return {
      ok: false,
      error: err instanceof Error ? err.message : 'Invalid JSON.',
    };
  }
}

export function parseObject(raw: string, label: string): JSONRecord {
  const parsed = parseJSONObject(raw);
  if (!parsed.ok) {
    throw new Error(
      parsed.error === 'Must be a JSON object.'
        ? `${label} must be a JSON object`
        : parsed.error
    );
  }
  return parsed.value;
}

function providerLabel(id: string, rows: LLMProvider[]) {
  return rows.find(row => row.id === id)?.key || 'none';
}

function modelLabel(id: string, rows: LLMModel[]) {
  return rows.find(row => row.id === id)?.key || 'none';
}

function routeLabel(id: string, rows: LLMRoute[]) {
  return rows.find(row => row.id === id)?.key || 'none';
}

function templateLabel(id: string, rows: WillowTemplate[]) {
  return rows.find(row => row.id === id)?.name || 'none';
}

function initialAgentRoleLabel(row: WillowInitialAgent) {
  const role = String(row.role || (row.is_router ? 'router' : 'worker')).trim();
  if (role === 'router') return 'Router';
  if (role === 'worker') return 'Worker';
  return role || 'Worker';
}

function templateSourceModelLabel(row: WillowTemplate, models: LLMModel[]) {
  if (row.model_id) {
    return modelLabel(row.model_id, models);
  }
  return row.model || 'unmapped';
}

function templateSourceProviderLabel(
  row: WillowTemplate,
  providers: LLMProvider[]
) {
  if (row.provider_id) {
    return providerLabel(row.provider_id, providers);
  }
  return row.provider_type || row.provider || 'unmapped';
}

function createTitle(kind: EntityKind) {
  return `Create ${kindLabel(kind)}`;
}

function editTitle(kind: EntityKind) {
  return `Edit ${kindLabel(kind)}`;
}

function kindLabel(kind: EntityKind) {
  return kind.replace('-', ' ');
}
